import { makeAutoObservable } from "mobx"

import { SearchMode } from "@framework/types/search-mode"
import { AnswerFormat } from "@framework/types/answer-format"

import { SearchEntityStore } from "./search-entity.store"
import { WorkbookSuggestionOption } from "./types"
import WorkbookEntityStore from "./workbook-entity.store"

export type SearchEntity = SearchEntityStore | WorkbookEntityStore

const SHOW_SUGGEST_IMPROVEMENTS_BANNER = "SHOW_SUGGEST_IMPROVEMENTS_BANNER"
const ENABLE_WEB_RESEARCH = "ENABLE_WEB_RESEARCH"
const SEARCH_MODE = "SEARCH_MODE"
const ANSWER_FORMAT = "ANSWER_FORMAT"

const isSearchMode = (value: string | null): value is SearchMode =>
  value === SearchMode.QUICK ||
  value === SearchMode.DEEP ||
  value === SearchMode.ONLINE

const isAnswerFormat = (value: string | null): value is AnswerFormat =>
  value === AnswerFormat.OVERVIEW || value === AnswerFormat.EMAIL

export class SearchFlowStore {
  // injections

  // constructor

  constructor() {
    this.showSuggestImprovementsBanner =
      localStorage.getItem(SHOW_SUGGEST_IMPROVEMENTS_BANNER) !== "false"
    this.isWebSearchEnabled =
      localStorage.getItem(ENABLE_WEB_RESEARCH) !== "false"
    const storedSearchMode = localStorage.getItem(SEARCH_MODE)
    this.searchMode = isSearchMode(storedSearchMode)
      ? storedSearchMode
      : SearchMode.QUICK
    const storedAnswerFormat = localStorage.getItem(ANSWER_FORMAT)
    this.answerFormat = isAnswerFormat(storedAnswerFormat)
      ? storedAnswerFormat
      : AnswerFormat.OVERVIEW

    makeAutoObservable(this)
  }

  // state
  oldThreadId?: string

  flowSequence: string[] = []

  flowData: Map<string, SearchEntity> = new Map()

  showSuggestImprovementsBanner: boolean

  verifiedAnswer: boolean = false

  questionId: string = ""

  selectedReportId: string | null = null

  reportCandidate: WorkbookSuggestionOption | null = null

  isWebSearchEnabled: boolean = false

  searchMode: SearchMode = SearchMode.QUICK

  answerFormat: AnswerFormat = AnswerFormat.OVERVIEW

  // actions

  setSearchMode = (mode: SearchMode) => {
    if (this.searchMode !== mode) {
      this.oldThreadId = undefined
    }
    this.searchMode = mode
    localStorage.setItem(SEARCH_MODE, mode)
  }

  setAnswerFormat = (format: AnswerFormat) => {
    this.answerFormat = format
    localStorage.setItem(ANSWER_FORMAT, format)
  }

  enableWebSearch = (value: boolean) => {
    this.isWebSearchEnabled = value
    localStorage.setItem(ENABLE_WEB_RESEARCH, value ? "true" : "false")
  }

  generateReport = (reportOption: WorkbookSuggestionOption | null) => {
    this.reportCandidate = reportOption
  }

  showReport = (reportId: string | null) => {
    this.selectedReportId = reportId
  }

  pushSearchInstance = (entity: SearchEntity) => {
    if (!this.flowSequence.includes(entity.id)) {
      this.flowSequence.push(entity.id)
    }
    this.flowData.set(entity.id, entity)
  }

  hasById = (blockId: string) => {
    const res = this.findById(blockId)
    return res != null
  }

  getById = (blockId: string) => {
    const res = this.findById(blockId)
    if (res == null) throw new Error("SearchFlow store is desynchronized")
    return res
  }

  findById = (searchId: string) => {
    return this.flowData.get(searchId)
  }

  hesSearchEntity = (searchId: string) => {
    return this.flowData.has(searchId)
  }

  reset = () => {
    this.flowSequence = []
    this.flowData = new Map()
    this.oldThreadId = undefined
    this.selectedReportId = null
  }

  setOldThreadId = (id: string) => {
    this.oldThreadId = id
  }

  setQuestionId = (id: string) => {
    this.questionId = id
  }

  isAnyLoading = () => {
    for (let i = this.flowSequence.length - 1; i >= 0; i -= 1) {
      if (this.flowData.get(this.flowSequence[i])?.isLoading) return true
    }
    return false
  }

  isLatestEntity = (entityId: string) => {
    return this.flowSequence.at(-1) === entityId
  }

  setSuggestImprovementsBanner = (show: boolean) => {
    this.showSuggestImprovementsBanner = show
    localStorage.setItem(
      SHOW_SUGGEST_IMPROVEMENTS_BANNER,
      show ? "true" : "false"
    )
  }

  get getLatestSearchBlock() {
    const { flowSequence } = this
    return () => {
      for (let i = flowSequence.length - 1; i >= 0; i -= 1) {
        const entity = this.findById(flowSequence[i])
        if (entity instanceof SearchEntityStore) return entity
      }
      return null
    }
  }
}

export default SearchFlowStore
